/**
 * Calendrier — grille mois/semaine calée sur le lundi, en UTC exclusivement (jamais de getter
 * local sur une date : le piège « semaine ISO côté client », plan §5, ligne « Semaine ISO »).
 *
 * Factorisé depuis `views/PlanningView.jsx` (plan tâche F1) pour être partagé avec la vue
 * d'ensemble (`views/overview/OverviewCalendar.jsx`).
 */

/** ISO `YYYY-MM-DD` d'une `Date`. */
export const toISO = (date) => date.toISOString().slice(0, 10);

/**
 * ISO `YYYY-MM-DD` du jour **local** de l'utilisateur — jamais `toISOString()` (UTC), qui fait
 * sauter la date d'un jour entre minuit et l'heure du décalage local (constaté à l'écran à 01h
 * du matin heure de Paris : la page affichait le 26 au lieu du 27).
 */
export function todayISO(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function parseISO(iso) {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day));
}

/** Nombre de jours (entier) entre deux ISO `YYYY-MM-DD` — `to − from`, peut être négatif. */
export function daysBetween(fromISO, toISOValue) {
  return Math.round((parseISO(toISOValue) - parseISO(fromISO)) / 86_400_000);
}

/** Lundi (ISO) de la semaine contenant `iso`. */
export function mondayOf(iso) {
  const date = parseISO(iso);
  const offset = (date.getUTCDay() + 6) % 7; // lundi = 0
  date.setUTCDate(date.getUTCDate() - offset);
  return toISO(date);
}

/** `iso` décalé de `n` jours (négatif accepté). */
export function addDays(iso, n) {
  const date = parseISO(iso);
  date.setUTCDate(date.getUTCDate() + n);
  return toISO(date);
}

/**
 * Abréviations de jours de la semaine (lundi → dimanche), via `Intl.DateTimeFormat` dans la
 * locale courante — jamais un tableau figé en français. 2026-01-05 est un lundi UTC connu :
 * point de départ neutre pour dériver les 7 jours.
 */
export function weekdayAbbreviations(locale) {
  const formatter = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' });
  return Array.from({ length: 7 }, (_, i) => formatter.format(new Date(Date.UTC(2026, 0, 5 + i))));
}

/** Grille de 6 semaines couvrant le mois (`month` 0-based), calée sur lundi. */
export function buildMonthGrid(year, month) {
  const first = new Date(Date.UTC(year, month, 1));
  const startOffset = (first.getUTCDay() + 6) % 7; // lundi = 0
  const start = new Date(first);
  start.setUTCDate(first.getUTCDate() - startOffset);
  return Array.from({ length: 42 }, (_, i) => {
    const day = new Date(start);
    day.setUTCDate(start.getUTCDate() + i);
    return day;
  });
}

/** Grille d'une seule semaine (lundi → dimanche) contenant `anchorISO`. */
export function buildWeekGrid(anchorISO) {
  const monday = mondayOf(anchorISO);
  return Array.from({ length: 7 }, (_, i) => parseISO(addDays(monday, i)));
}

/** Bornes `{ from, to }` de la grille de 42 jours du mois contenant `anchorISO`. */
export function monthBounds(anchorISO) {
  const date = parseISO(anchorISO);
  const grid = buildMonthGrid(date.getUTCFullYear(), date.getUTCMonth());
  return { from: toISO(grid[0]), to: toISO(grid[grid.length - 1]) };
}

/** Bornes `{ from, to }` de la semaine (lundi → dimanche) contenant `anchorISO`. */
export function weekBounds(anchorISO) {
  const grid = buildWeekGrid(anchorISO);
  return { from: toISO(grid[0]), to: toISO(grid[grid.length - 1]) };
}

/**
 * Déplace `anchorISO` d'une unité (`delta` = ±1) en mode `'month'` ou `'week'`. En mode mois,
 * le jour est conservé sauf s'il n'existe pas dans le mois cible (clamp sur le dernier jour —
 * 31 janvier + 1 mois → 28/29 février, jamais un débordement sur mars).
 */
export function shiftAnchor(anchorISO, mode, delta) {
  if (mode === 'week') return addDays(anchorISO, delta * 7);

  const date = parseISO(anchorISO);
  const day = date.getUTCDate();
  const targetYear = date.getUTCFullYear();
  const targetMonth = date.getUTCMonth() + delta;
  const targetFirst = new Date(Date.UTC(targetYear, targetMonth, 1));
  const lastDayOfTargetMonth = new Date(
    Date.UTC(targetFirst.getUTCFullYear(), targetFirst.getUTCMonth() + 1, 0),
  ).getUTCDate();
  const clampedDay = Math.min(day, lastDayOfTargetMonth);
  return toISO(
    new Date(Date.UTC(targetFirst.getUTCFullYear(), targetFirst.getUTCMonth(), clampedDay)),
  );
}
